"use client";

/**
 * FoilCard — Niche v3 Grid card shell with mood-driven hero-art zone.
 *
 * Replaces v1 `AccentCard` pattern (accent stripe left-edge) for the Grid
 * compare view. v1 AccentCard stays valid for other surfaces (Plan v1, Brand).
 *
 * Visual language (spec: wireframe-v3.md §2.5):
 *   - Background: mood-driven gradient + noise texture + radial vignette
 *   - Border: white/10 1px + inner ring white/5 (double-stroke foil)
 *   - topPick=true: amber shimmer bar on top edge + amber ring + extra glow
 *   - Hover (desktop pointer-fine): translate-y -4px + shadow-2xl + amber ring
 *   - Focus (keyboard): ring-2 amber-400 ring-offset-2
 *
 * Design tokens used:
 *   bg-surface-card, border-subtle — from shared-styles rule (no hex).
 *   getMoodGradient() → resolves mood → Tailwind gradient class
 *   (single source of truth: src/lib/niche-accent-tokens.ts).
 *
 * Accessibility:
 *   - If onClick given, wrapper renders as <button> with role=button via
 *     keyboard Enter/Space.
 *   - Parent may supply `aria-label` via ...rest if semantics need refining.
 *
 * Reduced-motion: shimmer disabled, translate-y disabled, ring-only hover.
 */

import { motion, useReducedMotion } from "framer-motion";
import type { KeyboardEvent, MouseEvent, ReactNode } from "react";
import { cn } from "@/lib/utils";
import { getMoodGradient } from "@/lib/niche-accent-tokens";
import type { NicheVisualMood } from "@/types/niche-v3";

interface FoilCardProps {
  children: ReactNode;
  /** Visual mood — drives hero-art ambient gradient. `null` → neutral fallback. */
  mood: NicheVisualMood | null;
  /** If true: amber shimmer bar + ring + extra glow treatment. */
  topPick?: boolean;
  /** Extra classes. */
  className?: string;
  /** Click handler — wrapper becomes activatable via mouse + keyboard. */
  onClick?: () => void;
  /** Accessible label when onClick supplied (required for interactive). */
  ariaLabel?: string;
}

export function FoilCard({
  children,
  mood,
  topPick = false,
  className,
  onClick,
  ariaLabel,
}: FoilCardProps) {
  const reduced = useReducedMotion();
  // Neutral fallback gradient for mood=null (keeps visual decision-driven
  // but doesn't break if backend skipped visualGradient).
  const moodGradient = mood
    ? getMoodGradient(mood)
    : "bg-gradient-to-br from-white/[0.04] to-white/[0.01]";

  const interactive = typeof onClick === "function";

  const handleKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!interactive) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick?.();
    }
  };

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (!interactive) return;
    // Allow nested buttons (CTA) to handle their own click without re-firing.
    if ((e.target as HTMLElement).closest("[data-foil-card-cta-stop]")) {
      return;
    }
    onClick?.();
  };

  return (
    <motion.div
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? ariaLabel : undefined}
      onClick={interactive ? handleClick : undefined}
      onKeyDown={interactive ? handleKey : undefined}
      whileHover={reduced || !interactive ? undefined : { y: -4 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className={cn(
        // Base shell
        "group relative flex flex-col overflow-hidden rounded-2xl bg-surface-card",
        // Double-stroke foil border
        "border border-white/10 ring-1 ring-white/5 ring-inset",
        // Hover / focus states (interactive only)
        interactive && "cursor-pointer transition-shadow duration-200",
        interactive && "hover:shadow-2xl hover:ring-amber-400/30",
        interactive &&
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-900",
        // Top-pick treatment
        topPick && "ring-amber-400/30 shadow-[0_0_40px_-10px_rgba(251,191,36,0.35)]",
        className,
      )}
    >
      {/* Mood-driven ambient background — sits below content, low-opacity. */}
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-0 opacity-40",
          moodGradient,
        )}
      />

      {/* Noise texture overlay (subtle grain) — SVG data URI, no external asset. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/></svg>\")",
        }}
      />

      {/* Radial vignette — darkens edges, pulls eye to center */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_0%,_transparent_55%,_rgba(0,0,0,0.35)_100%)]"
      />

      {/* Top-pick shimmer bar — 1-2px amber strip with running highlight */}
      {topPick && (
        <div
          aria-hidden
          className={cn(
            "pointer-events-none absolute inset-x-0 top-0 h-[2px] overflow-hidden",
            "bg-gradient-to-r from-transparent via-amber-400/80 to-transparent",
            !reduced && "animate-shimmer",
          )}
        />
      )}

      {/* Content layer — sits above ambient/vignette */}
      <div className="relative flex flex-1 flex-col">{children}</div>
    </motion.div>
  );
}
